import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, MessageCircle } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ContactForm = ({ preselectedService = "general" }) => {
  const { copy } = useLanguage();
  const c = copy.contact;
  const [form, setForm] = useState({ name: "", email: "", phone: "", service: preselectedService, mode: "online", message: "", privacy: false });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.privacy) return;
    setSending(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name,
        email: form.email,
        phone: form.phone || null,
        service: form.service,
        mode: form.mode,
        message: form.message,
      });
      toast.success(c.success);
      setForm({ name: "", email: "", phone: "", service: preselectedService, mode: "online", message: "", privacy: false });
    } catch {
      toast.error(c.error);
    } finally {
      setSending(false);
    }
  };

  const inputCls = "w-full rounded-xl border border-border bg-white px-4 py-3 text-sm text-ink placeholder:text-muted/60 outline-none transition-colors duration-300 focus:border-pine focus:ring-2 focus:ring-pine/15";

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="cf-name" className="mb-1.5 block text-xs font-mono uppercase tracking-widest text-sage">{c.name} *</label>
          <input id="cf-name" data-testid="contact-form-name" required minLength={2} value={form.name} onChange={set("name")} className={inputCls} placeholder={c.name} />
        </div>
        <div>
          <label htmlFor="cf-email" className="mb-1.5 block text-xs font-mono uppercase tracking-widest text-sage">{c.email} *</label>
          <input id="cf-email" data-testid="contact-form-email" type="email" required value={form.email} onChange={set("email")} className={inputCls} placeholder="tu@email.com" />
        </div>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="cf-phone" className="mb-1.5 block text-xs font-mono uppercase tracking-widest text-sage">{c.phone}</label>
          <input id="cf-phone" data-testid="contact-form-phone" value={form.phone} onChange={set("phone")} className={inputCls} placeholder="+34 ..." />
        </div>
        <div>
          <label htmlFor="cf-service" className="mb-1.5 block text-xs font-mono uppercase tracking-widest text-sage">{c.service}</label>
          <select id="cf-service" data-testid="contact-form-service" value={form.service} onChange={set("service")} className={inputCls}>
            {Object.entries(c.serviceOptions).map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </select>
        </div>
      </div>
      <div>
        <span className="mb-1.5 block text-xs font-mono uppercase tracking-widest text-sage">{c.mode}</span>
        <div className="flex gap-3" data-testid="contact-form-mode">
          {[["online", c.modeOnline], ["presencial", c.modePresential]].map(([val, label]) => (
            <button
              type="button"
              key={val}
              data-testid={`contact-mode-${val}`}
              onClick={() => setForm({ ...form, mode: val })}
              className={`flex-1 rounded-full border px-4 py-2.5 text-sm font-medium transition-colors duration-300 ${
                form.mode === val ? "border-pine bg-pine text-linen" : "border-border bg-white text-ink/70 hover:border-pine/50"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>
      <div>
        <label htmlFor="cf-message" className="mb-1.5 block text-xs font-mono uppercase tracking-widest text-sage">{c.message} *</label>
        <textarea id="cf-message" data-testid="contact-form-message" required minLength={5} rows={4} value={form.message} onChange={set("message")} className={`${inputCls} resize-none`} />
      </div>
      <label className="flex items-start gap-3 cursor-pointer" data-testid="contact-form-privacy-label">
        <input type="checkbox" data-testid="contact-form-privacy" required checked={form.privacy} onChange={set("privacy")} className="mt-1 h-4 w-4 accent-pine" />
        <span className="text-xs text-muted leading-relaxed">{c.privacy}</span>
      </label>
      <div className="flex flex-col sm:flex-row items-center gap-3 pt-1">
        <button
          type="submit"
          data-testid="contact-form-submit"
          disabled={sending || !form.privacy}
          className="inline-flex w-full sm:w-auto items-center justify-center gap-2 rounded-full bg-clay px-7 py-3.5 text-sm font-semibold text-white transition-all duration-300 hover:bg-clay-dark hover:shadow-lg disabled:opacity-50"
        >
          <Send size={16} /> {sending ? c.sending : c.submit}
        </button>
        <span className="text-xs text-muted">{c.or}</span>
        <a
          href="https://wa.me/34613234203"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="contact-whatsapp-link"
          className="inline-flex items-center gap-2 rounded-full border border-pine px-6 py-3.5 text-sm font-semibold text-pine transition-colors duration-300 hover:bg-pine hover:text-linen"
        >
          <MessageCircle size={16} /> {c.whatsapp}
        </a>
      </div>
    </form>
  );
};
